import React, { createContext, useContext, useMemo } from 'react';
import { ParameterizedURLData } from './types';

interface MacroEditorConfig {
  /** Date format for system date macros (e.g., 'yyyy-MM-dd', 'MM/dd/yyyy') */
  dateFormat?: string;
  /** Time unit for date operations (e.g., 'dd' for days, 'HH' for hours) */
  dateTimeUnit?: string;
  /** Optional parent dataset samples for macro replacement */
  parentDatasetSamples?: any[];
  /** Optional parameterized URL data for preview */
  parameterizedURLs?: ParameterizedURLData | null;
}

interface MacroEditorContextProviderProps extends MacroEditorConfig {
  children: React.ReactNode;
}

const MacroEditorConfigContext = createContext<MacroEditorConfig>({
  dateFormat: 'yyyy-MM-dd',
  dateTimeUnit: 'dd',
  parentDatasetSamples: undefined,
  parameterizedURLs: null,
});

export const MacroEditorConfigProvider: React.FC<MacroEditorContextProviderProps> = ({
  children,
  dateFormat = 'yyyy-MM-dd',
  dateTimeUnit = 'dd',
  parentDatasetSamples,
  parameterizedURLs,
}) => {
  const config = useMemo(
    () => ({
      dateFormat,
      dateTimeUnit,
      parentDatasetSamples,
      parameterizedURLs,
    }),
    [dateFormat, dateTimeUnit, parentDatasetSamples, parameterizedURLs],
  );

  return (
    <MacroEditorConfigContext.Provider value={config}>{children}</MacroEditorConfigContext.Provider>
  );
};

export const useMacroEditorConfig = (): MacroEditorConfig => {
  const context = useContext(MacroEditorConfigContext);

  if (!context) {
    throw new Error('useMacroEditorConfig must be used within MacroEditorConfigProvider');
  }

  return context;
};
